import { useState, useEffect, useCallback, useRef } from 'react'
import VoiceTextarea from '@/components/VoiceTextarea'
import { ComposeMessageModal } from '@/components/ComposeMessageModal'
import { MessaggiProgrammati } from './tabs/MessaggiProgrammati'
import { MessaggiInterni } from './tabs/MessaggiInterni'
import {
  Table,
  Tag,
  Input,
  Select,
  Space,
  Modal,
  Button,
  Typography,
  Badge,
  message,
  Spin,
  Tabs,
  theme,
} from 'antd'
import {
  MessageOutlined,
  MailOutlined,
  PhoneOutlined,
  SendOutlined,
  DownOutlined,
  RightOutlined,
  ExclamationCircleFilled,
} from '@ant-design/icons'
import type { ColumnsType } from 'antd/es/table'
import axiosInstance from '@/lib/axios'
import { usePermissions } from '@/hooks/usePermissions'
import { usePageTitle } from '@/contexts/PageTitleContext'

const { Text } = Typography
const { Option } = Select

// ─── Types ───────────────────────────────────────────────────────────────────

interface Comunicazione {
  id: number
  tipo: 'whatsapp' | 'sms' | 'email'
  direzione: 'outbound' | 'inbound'
  stato: 'inviato' | 'fallito' | 'ricevuto'
  destinatario_nome?: string
  destinatario_cognome?: string
  destinatario_numero?: string
  testo: string
  utente_mittente_id?: number
  utente_mittente_nome?: string
  utente_mittente_cognome?: string
  risposta_a_id?: number
  letta: 'si' | 'no'
  created_at: string
  has_replies: boolean
  replies_count: number
  has_unread_replies: boolean
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

const formatDateTime = (isoString: string): string => {
  try {
    const d = new Date(isoString)
    const day = String(d.getDate()).padStart(2, '0')
    const month = String(d.getMonth() + 1).padStart(2, '0')
    const year = d.getFullYear()
    const hours = String(d.getHours()).padStart(2, '0')
    const minutes = String(d.getMinutes()).padStart(2, '0')
    return `${day}/${month}/${year} ${hours}:${minutes}`
  } catch {
    return isoString
  }
}

const getDestinatarioLabel = (record: Comunicazione): string => {
  const nome = `${record.destinatario_nome || ''} ${record.destinatario_cognome || ''}`.trim()
  return nome || record.destinatario_numero || '-'
}

const tipoConfig: Record<
  Comunicazione['tipo'],
  { color: string; icon: React.ReactNode; label: string }
> = {
  whatsapp: { color: 'green', icon: <MessageOutlined />, label: 'WhatsApp' },
  sms: { color: 'orange', icon: <PhoneOutlined />, label: 'SMS' },
  email: { color: 'blue', icon: <MailOutlined />, label: 'Email' },
}

// ─── Bubble component per thread modal ───────────────────────────────────────

const Bubble: React.FC<{ msg: Comunicazione }> = ({ msg }) => {
  const { token } = theme.useToken()
  const isOutbound = msg.direzione === 'outbound'
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: isOutbound ? 'flex-end' : 'flex-start',
        marginBottom: 12,
      }}
    >
      <div
        style={{
          maxWidth: '70%',
          padding: '8px 12px',
          borderRadius: isOutbound ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
          background: isOutbound ? token.colorPrimary : token.colorFillSecondary,
          color: isOutbound ? '#fff' : token.colorText,
        }}
      >
        <div style={{ fontSize: 13 }}>{msg.testo}</div>
        <div style={{ fontSize: 11, marginTop: 4, opacity: 0.75, textAlign: isOutbound ? 'right' : 'left' }}>
          {formatDateTime(msg.created_at)}
          {isOutbound && msg.utente_mittente_nome && (
            <span> &mdash; {msg.utente_mittente_nome} {msg.utente_mittente_cognome || ''}</span>
          )}
        </div>
      </div>
    </div>
  )
}

// ─── Riga espansa: thread inline ─────────────────────────────────────────────

const ThreadInline: React.FC<{
  rootId: number
  canSend: boolean
  onRead: () => void
}> = ({ rootId, canSend, onRead }) => {
  const { token } = theme.useToken()
  const [thread, setThread] = useState<Comunicazione[]>([])
  const [loading, setLoading] = useState(true)
  const [replyText, setReplyText] = useState('')
  const [replySending, setReplySending] = useState(false)

  // useRef evita che onRead (funzione inline) causi un loop infinito nel useEffect
  const onReadRef = useRef(onRead)
  useEffect(() => { onReadRef.current = onRead }, [onRead])

  const loadThread = useCallback(async () => {
    setLoading(true)
    try {
      const res = await axiosInstance.get(`/comunicazioni/${rootId}/thread`)
      const data = Array.isArray(res.data) ? res.data : []
      setThread(data)
      // Segna come letti tutti gli inbound non letti
      for (const msg of data) {
        if (msg.direzione === 'inbound' && msg.letta === 'no') {
          axiosInstance.patch(`/comunicazioni/${msg.id}/letta`).catch(() => {})
        }
      }
      onReadRef.current()
    } catch {
      setThread([])
    } finally {
      setLoading(false)
    }
  }, [rootId])

  useEffect(() => {
    loadThread()
  }, [loadThread])

  const handleReply = async () => {
    if (!replyText.trim()) return
    setReplySending(true)
    try {
      await axiosInstance.post(`/comunicazioni/${rootId}/rispondi`, { testo: replyText.trim() })
      message.success('Risposta inviata')
      setReplyText('')
      loadThread()
    } catch {
      // gestito dall'interceptor
    } finally {
      setReplySending(false)
    }
  }

  const replies = thread.filter((m) => m.id !== rootId)
  const latestIsInbound = replies.length > 0 && replies[replies.length - 1].direzione === 'inbound'
  const showReplyBox = canSend && (latestIsInbound || thread.length === 1)

  if (loading) {
    return (
      <div style={{ padding: '4px 16px' }}>
        <Spin size="small" /> <Text type="secondary" style={{ marginLeft: 6, fontSize: 12 }}>Caricamento...</Text>
      </div>
    )
  }

  return (
    <div style={{ padding: '4px 16px 8px', background: token.colorFillAlter, borderRadius: 4 }}>
      {replies.length === 0 ? (
        <Text type="secondary" style={{ fontSize: 12 }}>Nessuna risposta ancora</Text>
      ) : (
        replies.map((msg) => (
          <div
            key={msg.id}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              marginBottom: 2,
            }}
          >
            <div style={{ color: token.colorTextQuaternary, fontSize: 11, flexShrink: 0 }}>↳</div>
            <div
              style={{
                flex: 1,
                padding: '2px 8px',
                borderRadius: 4,
                background: msg.direzione === 'inbound' ? token.colorWarningBg : token.colorInfoBg,
                border: `1px solid ${msg.direzione === 'inbound' ? token.colorWarningBorder : token.colorInfoBorder}`,
                fontSize: 12,
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                minWidth: 0,
              }}
            >
              <Text type="secondary" style={{ fontSize: 10, flexShrink: 0 }}>{formatDateTime(msg.created_at)}</Text>
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{msg.testo}</span>
            </div>
          </div>
        ))
      )}

      {showReplyBox && (
        <div style={{ marginTop: 6, display: 'flex', gap: 6 }}>
          <VoiceTextarea
            rows={2}
            minHeight={60}
            maxHeight={150}
            placeholder="Scrivi una risposta…"
            label="Risposta"
            compact
            value={replyText}
            onChange={(v) => setReplyText(v)}
          />
          <Button
            type="primary"
            icon={<SendOutlined />}
            loading={replySending}
            disabled={!replyText.trim()}
            onClick={handleReply}
          >
            Invia
          </Button>
        </div>
      )}
    </div>
  )
}

// ─── Main page ────────────────────────────────────────────────────────────────

const ComunicazioniPage: React.FC = () => {
  const { setTitle } = usePageTitle()
  const { hasPermission } = usePermissions()
  const canViewEsterne = hasPermission('comunicazioni.view')
  const canManageProgrammati = hasPermission('comunicazioni.programmati')

  useEffect(() => {
    setTitle('Comunicazioni')
  }, [setTitle])

  const tabItems = [
    ...(canViewEsterne ? [{
      key: 'esterne',
      label: 'WhatsApp / SMS',
      children: <EsterneTab />,
    }] : []),
    ...(canManageProgrammati ? [{
      key: 'programmate',
      label: 'Programmate',
      children: <MessaggiProgrammati />,
    }] : []),
    {
      key: 'interne',
      label: 'Interne',
      children: <MessaggiInterni />,
    },
  ]

  return (
    <div>
      <Tabs
        defaultActiveKey={canViewEsterne ? 'esterne' : 'interne'}
        style={{ marginBottom: 0 }}
        items={tabItems}
      />
    </div>
  )
}

// ─── Tab Esterne (estratto) ───────────────────────────────────────────────────

const EsterneTab: React.FC = () => {
  const { token } = theme.useToken()
  const { hasPermission } = usePermissions()
  const canSend = hasPermission('comunicazioni.send')

  const [tipoFilter, setTipoFilter] = useState<string>('tutti')
  const [searchText, setSearchText] = useState('')
  const [comunicazioni, setComunicazioni] = useState<Comunicazione[]>([])
  const [loading, setLoading] = useState(false)
  const [expandedRowKeys, setExpandedRowKeys] = useState<number[]>([])
  const [composeOpen, setComposeOpen] = useState(false)

  // Thread modal
  const [threadVisible, setThreadVisible] = useState(false)
  const [thread, setThread] = useState<Comunicazione[]>([])
  const [threadLoading, setThreadLoading] = useState(false)
  const [selectedRecord, setSelectedRecord] = useState<Comunicazione | null>(null)
  const [replyText, setReplyText] = useState('')
  const [replySending, setReplySending] = useState(false)

  const fetchRef = useRef(0)

  const fetchComunicazioni = useCallback(async () => {
    const myFetch = ++fetchRef.current
    setLoading(true)
    try {
      const params: Record<string, string> = { roots_only: 'true' }
      if (tipoFilter !== 'tutti') params.tipo = tipoFilter
      if (searchText) params.search = searchText
      const res = await axiosInstance.get('/comunicazioni/', { params })
      if (myFetch === fetchRef.current) {
        setComunicazioni(Array.isArray(res.data) ? res.data : [])
      }
    } catch {
      // gestito dall'interceptor
    } finally {
      if (myFetch === fetchRef.current) setLoading(false)
    }
  }, [tipoFilter, searchText])

  useEffect(() => {
    fetchComunicazioni()
  }, [fetchComunicazioni])

  const handleOpenModal = async (record: Comunicazione) => {
    setSelectedRecord(record)
    setReplyText('')
    setThreadVisible(true)
    setThreadLoading(true)
    if (record.letta === 'no') {
      axiosInstance.patch(`/comunicazioni/${record.id}/letta`).catch(() => {})
      setComunicazioni((prev) =>
        prev.map((c) => (c.id === record.id ? { ...c, letta: 'si', has_unread_replies: false } : c))
      )
    }
    try {
      const res = await axiosInstance.get(`/comunicazioni/${record.id}/thread`)
      setThread(Array.isArray(res.data) ? res.data : [record])
    } catch {
      setThread([record])
    } finally {
      setThreadLoading(false)
    }
  }

  const handleCloseThread = () => {
    setThreadVisible(false)
    setThread([])
    setSelectedRecord(null)
    setReplyText('')
  }

  const handleReplyModal = async () => {
    if (!selectedRecord || !replyText.trim()) return
    setReplySending(true)
    try {
      await axiosInstance.post(`/comunicazioni/${selectedRecord.id}/rispondi`, { testo: replyText.trim() })
      message.success('Risposta inviata')
      setReplyText('')
      const res = await axiosInstance.get(`/comunicazioni/${selectedRecord.id}/thread`)
      setThread(Array.isArray(res.data) ? res.data : [])
      fetchComunicazioni()
    } catch {
      // gestito dall'interceptor
    } finally {
      setReplySending(false)
    }
  }

  const latestMessage = thread.length > 0 ? thread[thread.length - 1] : null
  const showReplyBox = canSend && latestMessage?.direzione === 'inbound'

  const columns: ColumnsType<Comunicazione> = [
    {
      title: 'Tipo',
      dataIndex: 'tipo',
      key: 'tipo',
      width: 120,
      render: (tipo: Comunicazione['tipo']) => {
        const cfg = tipoConfig[tipo]
        return cfg ? <Tag color={cfg.color} icon={cfg.icon}>{cfg.label}</Tag> : <Tag>{tipo}</Tag>
      },
    },
    {
      title: 'Destinatario',
      key: 'destinatario',
      render: (_, record) => {
        const nome = `${record.destinatario_nome || ''} ${record.destinatario_cognome || ''}`.trim()
        if (nome && record.destinatario_numero) {
          return <span>{nome} <Text type="secondary" style={{ fontSize: 11 }}>({record.destinatario_numero})</Text></span>
        }
        return <span>{nome || record.destinatario_numero || '-'}</span>
      },
    },
    {
      title: 'Anteprima',
      dataIndex: 'testo',
      key: 'testo',
      render: (testo: string, record) => (
        <Space size={4}>
          {record.direzione === 'inbound' && record.letta === 'no' && (
            <ExclamationCircleFilled style={{ color: token.colorError, fontSize: 14 }} />
          )}
          <Text type="secondary">
            {testo && testo.length > 70 ? `${testo.slice(0, 70)}…` : testo || '-'}
          </Text>
        </Space>
      ),
    },
    {
      title: 'Mittente',
      key: 'mittente',
      width: 140,
      render: (_, record) => {
        const nome = `${record.utente_mittente_nome || ''} ${record.utente_mittente_cognome || ''}`.trim()
        return nome || (record.direzione === 'inbound' ? <Text type="secondary">Cliente</Text> : '-')
      },
    },
    {
      title: 'Risposte',
      key: 'risposte',
      width: 100,
      render: (_, record) => {
        if (!record.has_replies) return <Text type="secondary" style={{ fontSize: 12 }}>—</Text>
        return (
          <Badge
            count={record.has_unread_replies ? record.replies_count : 0}
            showZero={false}
            color="red"
          >
            <Tag color={record.has_unread_replies ? 'red' : 'default'}>
              {record.replies_count} {record.replies_count === 1 ? 'risposta' : 'risposte'}
            </Tag>
          </Badge>
        )
      },
    },
    {
      title: 'Data/Ora',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 140,
      render: (val: string) => formatDateTime(val),
      defaultSortOrder: 'descend',
      sorter: (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
    },
  ]

  return (
    <div>
      {/* Filter bar */}
      <div style={{ marginBottom: 16, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <Select value={tipoFilter} onChange={setTipoFilter} style={{ width: 140 }}>
          <Option value="tutti">Tutti i tipi</Option>
          <Option value="whatsapp">WhatsApp</Option>
          <Option value="sms">SMS</Option>
          <Option value="email">Email</Option>
        </Select>

        <Input.Search
          placeholder="Cerca comunicazione..."
          onSearch={setSearchText}
          allowClear
          style={{ flex: '1 1 200px', maxWidth: 320 }}
        />

        {canSend && (
          <Button
            type="primary"
            icon={<SendOutlined />}
            onClick={() => setComposeOpen(true)}
            style={{ marginLeft: 'auto' }}
          >
            Nuovo messaggio
          </Button>
        )}
      </div>

      <ComposeMessageModal
        open={composeOpen}
        onCancel={() => setComposeOpen(false)}
        onSent={() => { setComposeOpen(false); fetchComunicazioni() }}
      />

      {/* Tabella con righe espandibili */}
      <Table
        columns={columns}
        dataSource={comunicazioni}
        rowKey="id"
        size="small"
        loading={loading}
        expandable={{
          expandedRowKeys,
          onExpand: (expanded, record) => {
            if (expanded) {
              setExpandedRowKeys((prev) => [...prev, record.id])
            } else {
              setExpandedRowKeys((prev) => prev.filter((k) => k !== record.id))
            }
          },
          expandIcon: ({ expanded, onExpand, record }) => {
            if (!record.has_replies) {
              // Inbound autonomo: apre modal
              if (record.direzione === 'inbound') {
                return (
                  <Button
                    type="text"
                    size="small"
                    icon={<ExclamationCircleFilled style={{ color: record.letta === 'no' ? token.colorError : token.colorTextTertiary }} />}
                    onClick={(e) => { e.stopPropagation(); handleOpenModal(record) }}
                  />
                )
              }
              return <span style={{ display: 'inline-block', width: 24 }} />
            }
            return (
              <Button
                type="text"
                size="small"
                icon={
                  record.has_unread_replies ? (
                    <Badge dot color="red">
                      {expanded ? <DownOutlined /> : <RightOutlined />}
                    </Badge>
                  ) : (
                    expanded ? <DownOutlined /> : <RightOutlined />
                  )
                }
                onClick={(e) => { e.stopPropagation(); onExpand(record, e) }}
              />
            )
          },
          expandedRowRender: (record) => (
            <ThreadInline
              rootId={record.id}
              canSend={canSend}
              onRead={() => {
                setComunicazioni((prev) =>
                  prev.map((c) =>
                    c.id === record.id ? { ...c, has_unread_replies: false } : c
                  )
                )
              }}
            />
          ),
          rowExpandable: (record) => record.has_replies,
        }}
        onRow={(record) => ({
          style: {
            cursor: 'pointer',
            background: record.has_unread_replies || (record.direzione === 'inbound' && record.letta === 'no')
              ? '#fffbe6'
              : undefined,
          },
          onClick: () => handleOpenModal(record),
        })}
        pagination={{
          showTotal: (total) => `Totale ${total} conversazioni`,
          showSizeChanger: true,
          pageSizeOptions: ['10', '20', '50'],
          defaultPageSize: 20,
        }}
      />

      {/* Modal per inbound autonomi */}
      <Modal
        title={
          selectedRecord ? (
            <Space>
              {tipoConfig[selectedRecord.tipo]?.icon}
              <span>{getDestinatarioLabel(selectedRecord)}</span>
            </Space>
          ) : 'Messaggio'
        }
        open={threadVisible}
        onCancel={handleCloseThread}
        footer={null}
        width="min(600px, 95vw)"
        styles={{ body: { padding: '16px 20px' } }}
      >
        <div style={{ minHeight: 100, maxHeight: 400, overflowY: 'auto', padding: '8px 0' }}>
          {threadLoading ? (
            <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>
          ) : (
            thread.map((msg) => <Bubble key={msg.id} msg={msg} />)
          )}
        </div>
        {!threadLoading && showReplyBox && (
          <div style={{ marginTop: 16, borderTop: '1px solid #f0f0f0', paddingTop: 12 }}>
            <VoiceTextarea
              rows={3}
              minHeight={80}
              maxHeight={200}
              placeholder="Scrivi una risposta…"
              label="Risposta"
              compact
              value={replyText}
              onChange={(v) => setReplyText(v)}
            />
            <div style={{ marginTop: 8, textAlign: 'right' }}>
              <Button
                type="primary"
                icon={<SendOutlined />}
                loading={replySending}
                disabled={!replyText.trim()}
                onClick={handleReplyModal}
              >
                Invia risposta
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  )
}

export default ComunicazioniPage
